import React, { useMemo, useState } from 'react';
import {
  FaSave,
  FaRedo,
  FaCog,
  FaEye,
  FaGripVertical,
  FaThLarge,
  FaClipboardList,
  FaUsers,
  FaTasks,
  FaFileAlt,
  FaCheckCircle,
  FaDollarSign,
  FaBell,
  FaCalendarAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  setPrimaryButtonColorPreference,
  setSecondaryButtonColorPreference,
  setBrandColorPreference,
  setSidebarStylePreference,
  setThemePreference,
  THEME,
} from '../utils/portalTheme';
import '../styles/AdminDashboardPreferences.css';

const STORAGE_KEY = 'dashboard-preferences-v1';

const DEFAULT_PREFS = {
  themeMode: 'light',
  brandColor: 'indigo',
  primaryButtonColor: 'indigo',
  secondaryButtonColor: 'blue',
  sidebarStyle: 'brand',
  defaultView: "Today's Tasks",
  widgets: {
    assignedProjects: true,
    todaysJobs: true,
    teamAvailability: true,
    activeTasks: true,
    recentReports: true,
    pendingApprovals: false,
    paymentSummary: true,
    notifications: true,
    calendarSchedule: false,
  },
};

const WIDGETS = [
  { key: 'assignedProjects', label: 'Assigned Projects', icon: <FaThLarge /> },
  { key: 'todaysJobs', label: "Today's Jobs", icon: <FaClipboardList /> },
  { key: 'teamAvailability', label: 'Team Availability', icon: <FaUsers />, tag: 'Manager Only' },
  { key: 'activeTasks', label: 'Active Tasks', icon: <FaTasks /> },
  { key: 'recentReports', label: 'Recent Reports', icon: <FaFileAlt /> },
  { key: 'pendingApprovals', label: 'Pending Approvals', icon: <FaCheckCircle />, tag: 'Manager Only' },
  { key: 'paymentSummary', label: 'Payment Summary', icon: <FaDollarSign /> },
  { key: 'notifications', label: 'Notifications', icon: <FaBell /> },
  { key: 'calendarSchedule', label: 'Calendar Schedule', icon: <FaCalendarAlt /> },
];

const BRAND_COLORS = [
  { key: 'indigo', label: 'Default Indigo', value: '#2537c6' },
  { key: 'blue', label: 'Blue', value: '#2f68ea' },
  { key: 'green', label: 'Green', value: '#089c69' },
  { key: 'purple', label: 'Purple', value: '#7b3fe4' },
  { key: 'orange', label: 'Orange', value: '#ee5f00' },
];

const BUTTON_COLORS = [
  { key: 'indigo', label: 'Indigo', value: '#2537c6' },
  { key: 'blue', label: 'Blue', value: '#2f68ea' },
  { key: 'green', label: 'Green', value: '#089c69' },
  { key: 'purple', label: 'Purple', value: '#7b3fe4' },
  { key: 'orange', label: 'Orange', value: '#ee5f00' },
];

const readPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      widgets: { ...DEFAULT_PREFS.widgets, ...(parsed.widgets || {}) },
    };
  } catch {
    return DEFAULT_PREFS;
  }
};

const toTitleCase = (value = '') =>
  String(value)
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export default function DashboardPreferences() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(readPrefs);
  const [appliedPrefs, setAppliedPrefs] = useState(readPrefs);
  const [saved, setSaved] = useState(false);

  const enabledCount = useMemo(
    () => Object.values(prefs.widgets).filter(Boolean).length,
    [prefs.widgets],
  );

  const updateWidget = (key) =>
    setPrefs((prev) => ({
      ...prev,
      widgets: { ...prev.widgets, [key]: !prev.widgets[key] },
    }));

  const saveSettings = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setThemePreference(prefs.themeMode === THEME.DARK ? THEME.DARK : THEME.LIGHT);
    setBrandColorPreference(prefs.brandColor);
    setPrimaryButtonColorPreference(prefs.primaryButtonColor);
    setSecondaryButtonColorPreference(prefs.secondaryButtonColor);
    setSidebarStylePreference(prefs.sidebarStyle);
    setAppliedPrefs(prefs);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const resetSettings = () => {
    setPrefs(DEFAULT_PREFS);
  };

  const selectedBrandColor = useMemo(
    () => BRAND_COLORS.find((color) => color.key === prefs.brandColor) || BRAND_COLORS[0],
    [prefs.brandColor],
  );

  return (
    <div className="dp-page">
      <div className="dp-header">
        <div>
          <h1>Dashboard Preferences</h1>
          <p>Customize how your dashboard looks and what information you want to see.</p>
        </div>
        <button type="button" className="dp-back-btn" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <div className="dp-content">
        <div className="dp-main">
          <section className="dp-card">
            <div className="dp-section-head">
              <span className="dp-section-icon">
                <FaCog />
              </span>
              <div>
                <h2>Theme Customization</h2>
                <p className="dp-subtitle">Personalize your dashboard appearance</p>
              </div>
            </div>

            <div className="dp-row">
              <label>Theme Mode</label>
              <div className="dp-choice-grid two">
                <button type="button" className={`dp-mode-btn ${prefs.themeMode === 'light' ? 'active' : ''}`} onClick={() => setPrefs((p) => ({ ...p, themeMode: 'light' }))}>
                  <strong>Light Mode</strong>
                  <span>Bright and clean</span>
                </button>
                <button type="button" className={`dp-mode-btn ${prefs.themeMode === 'dark' ? 'active' : ''}`} onClick={() => setPrefs((p) => ({ ...p, themeMode: 'dark' }))}>
                  <strong>Dark Mode</strong>
                  <span>Easy on the eyes</span>
                </button>
              </div>
            </div>

            <div className="dp-row">
              <label>Brand Color</label>
              <div className="dp-choice-grid five">
                {BRAND_COLORS.map((color) => (
                  <button key={color.key} type="button" className={`dp-color-btn ${prefs.brandColor === color.key ? 'active' : ''}`} onClick={() => setPrefs((p) => ({ ...p, brandColor: color.key }))}>
                    <span className="dp-color-chip" style={{ background: color.value }} />
                    <span>{color.label}</span>
                  </button>
                ))}
              </div>
              <p className="dp-help-text">
                Custom Color Picker
                <span>Match your company branding</span>
              </p>
            </div>

            <div className="dp-row">
              <label>Primary Button Color</label>
              <div className="dp-choice-grid five">
                {BUTTON_COLORS.map((color) => (
                  <button
                    key={`primary-${color.key}`}
                    type="button"
                    className={`dp-color-btn ${prefs.primaryButtonColor === color.key ? 'active' : ''}`}
                    onClick={() => setPrefs((p) => ({ ...p, primaryButtonColor: color.key }))}
                  >
                    <span className="dp-color-chip" style={{ background: color.value }} />
                    <span>{color.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="dp-row">
              <label>Secondary Button Color</label>
              <div className="dp-choice-grid five">
                {BUTTON_COLORS.map((color) => (
                  <button
                    key={`secondary-${color.key}`}
                    type="button"
                    className={`dp-color-btn ${prefs.secondaryButtonColor === color.key ? 'active' : ''}`}
                    onClick={() => setPrefs((p) => ({ ...p, secondaryButtonColor: color.key }))}
                  >
                    <span className="dp-color-chip" style={{ background: color.value }} />
                    <span>{color.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <p className="dp-help-text">
              Primary and secondary colors apply across the admin portal (nav, sidebar, dashboard, and
              related flows). For new screens, use <code>portal-btn-primary</code> or{' '}
              <code>portal-btn-secondary</code> on buttons.
            </p>

            <div className="dp-row">
              <label>Sidebar Style</label>
              <div className="dp-choice-grid three">
                <button type="button" className={`dp-sidebar-btn ${prefs.sidebarStyle === 'light' ? 'active' : ''}`} onClick={() => setPrefs((p) => ({ ...p, sidebarStyle: 'light' }))}>
                  <span className="dp-sidebar-demo light" />
                  <span>Light Sidebar</span>
                </button>
                <button type="button" className={`dp-sidebar-btn ${prefs.sidebarStyle === 'dark' ? 'active' : ''}`} onClick={() => setPrefs((p) => ({ ...p, sidebarStyle: 'dark' }))}>
                  <span className="dp-sidebar-demo dark" />
                  <span>Dark Sidebar</span>
                </button>
                <button type="button" className={`dp-sidebar-btn ${prefs.sidebarStyle === 'brand' ? 'active' : ''}`} onClick={() => setPrefs((p) => ({ ...p, sidebarStyle: 'brand' }))}>
                  <span className="dp-sidebar-demo brand" />
                  <span>Brand Color</span>
                </button>
              </div>
            </div>
          </section>

          <section className="dp-card">
            <div className="dp-section-head">
              <span className="dp-section-icon">
                <FaThLarge />
              </span>
              <div>
                <h2>Dashboard Widgets</h2>
                <p className="dp-subtitle">Control which widgets appear on your dashboard</p>
              </div>
            </div>
            <div className="dp-widget-list">
              {WIDGETS.map((widget) => (
                <div key={widget.key} className="dp-widget-row">
                  <div className="dp-widget-info">
                    <span className="dp-widget-drag">
                      <FaGripVertical />
                    </span>
                    <span className="dp-widget-icon">{widget.icon}</span>
                    <span>{widget.label}</span>
                    {widget.tag ? <em>{widget.tag}</em> : null}
                  </div>
                  <button type="button" className={`dp-toggle ${prefs.widgets[widget.key] ? 'on' : ''}`} onClick={() => updateWidget(widget.key)} />
                </div>
              ))}
            </div>
          </section>

          <div className="dp-actions">
            <button type="button" className="dp-reset" onClick={resetSettings}>
              <FaRedo /> Reset to Default
            </button>
            <button type="button" className="dp-save" onClick={saveSettings}>
              <FaSave /> Save Dashboard Settings
            </button>
          </div>
          {saved ? <p className="dp-saved">Settings saved.</p> : null}
        </div>

        <aside className="dp-preview">
          <div className="dp-section-head">
            <span className="dp-section-icon">
              <FaEye />
            </span>
            <div>
              <h3>Live Preview</h3>
              <p>See your changes in real-time</p>
            </div>
          </div>
          <div className="dp-preview-shell">
            <div className="dp-preview-topbar" style={{ background: selectedBrandColor.value }}>
              <span>Dashboard</span>
              <div>◦ ◦</div>
            </div>
            <div className={`dp-preview-layout ${prefs.sidebarStyle} ${prefs.themeMode === 'dark' ? 'dark-mode' : ''}`}>
              <div
                className="dp-preview-sidebar"
                style={
                  prefs.sidebarStyle === 'brand'
                    ? {
                        background: `linear-gradient(130deg, ${selectedBrandColor.value}, ${selectedBrandColor.value}cc)`,
                      }
                    : undefined
                }
              >
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="dp-preview-grid">
                <div>Assigned Projects</div>
                <div>Today&apos;s Jobs</div>
                <div>Team Availability</div>
                <div>Active Tasks</div>
              </div>
            </div>
            <div className="dp-preview-note">
              Live Preview: Changes you make will update this preview in real-time. Save your settings
              to apply them to your actual dashboard.
            </div>
          </div>
          <div className="dp-applied-summary">
            <h4>Applied Selection</h4>
            <div className="dp-applied-tags">
              <span className="dp-applied-tag">Theme: {toTitleCase(appliedPrefs.themeMode)}</span>
              <span className="dp-applied-tag">Brand: {toTitleCase(appliedPrefs.brandColor)}</span>
              <span className="dp-applied-tag">Primary Btn: {toTitleCase(appliedPrefs.primaryButtonColor)}</span>
              <span className="dp-applied-tag">Secondary Btn: {toTitleCase(appliedPrefs.secondaryButtonColor)}</span>
              <span className="dp-applied-tag">Sidebar: {toTitleCase(appliedPrefs.sidebarStyle)}</span>
            </div>
          </div>
          <div className="dp-preview-meta">Enabled widgets: {enabledCount}</div>
        </aside>
      </div>
    </div>
  );
}
